import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { devtools, subscribeWithSelector } from 'zustand/middleware'
import { composeMiddleware } from 'project-config/utils'
import { StoreSet, StoreGet } from 'project-config/types'
import { IGlobalState, IGlobalStore, IGlobalGetter, IGlobalAction, UserInfo } from './type'
import {
  UserType,
  STORAGE,
  Zone,
  BusinessType,
  InfoStatus,
  AccountType,
  UserLevel,
  UserCertifiedStatus,
} from 'project-config/constants'
import { GlobalAPI } from './service'
import { accAdd } from 'project-config/utils/acc'
import { getTranslateNsT } from '@/utils/get-translate-nst'

const t = getTranslateNsT('common')

const initialState = (): IGlobalState => ({
  userInfo: undefined,
  studentCertifyInfo: undefined,
  vocherInfo: undefined,
  recentExpireDate: '',
  shareKey: '',
})

const actions = (set: StoreSet<IGlobalStore>, get: StoreGet<IGlobalStore>): IGlobalAction => ({
  fetchUserData: async (headers?: object) => {
    try {
      const { userInfo } = get()
      const { data, code } = await GlobalAPI.queryUser(headers)
      const _data = data ?? userInfo
      set({
        userInfo: _data,
      })
      localStorage.setItem(STORAGE.USER_BUSINESS_TYPE, String(_data ? _data?.businessType : ''))
      return { data, code }
    } catch (error) {
      sessionStorage.setItem(STORAGE.USER_NETWORK_ERROR, JSON.stringify(error))
      return { data: undefined, code: -1 }
    }
  },
  updateUserInfo: (userInfo: { key: keyof UserInfo; value: number | string | null }) => {
    set((state) => {
      if (state.userInfo) {
        state.userInfo = { ...state.userInfo, ...userInfo }
      }
    })
  },
  updateShareKey: (shareKey: string) => {
    set({ shareKey })
  },
})

const getters = (get: StoreGet<IGlobalStore>): IGlobalGetter => {
  return {
    isPersonal: () => {
      return get().userInfo?.userType === UserType.Personal
    },
    isDomestic: () => {
      return get().userInfo?.zone === Zone.Domestic
    },
    isPictureUser: () => {
      return get().userInfo?.businessType === BusinessType.Picture
    },
    isSubAccount: () => {
      return get().userInfo?.accountType === AccountType.Sub
    },
    currencySymbol: () => {
      return get().isDomestic() ? '¥' : 'US$'
    },
    userBalance: () => {
      const { isDomestic, userInfo } = get()
      return (isDomestic() ? userInfo?.rmbbalance : userInfo?.usdbalance) || 0
    },
    userCredit: () => {
      const { userInfo } = get()
      if ((userInfo?.rmbbalance ?? 0) >= 0) {
        return userInfo?.credit ?? 0
      } else if ((userInfo?.credit ?? 0) + (userInfo?.rmbbalance ?? 0) > 0) {
        return accAdd(userInfo?.credit ?? 0, userInfo?.rmbbalance ?? 0)
      } else {
        return 0
      }
    },
    userCouponCount: () => {
      return get().userInfo?.commonCouponCount || 0
    },
    userCouponPrice: () => {
      return get().userInfo?.commonCoupon || 0
    },
    userQYCouponPrice: () => {
      return get().userInfo?.qyCoupon || 0
    },
    userQYCouponCount: () => {
      return get().userInfo?.qyCouponCount || 0
    },
    userLevelTranslate: () => {
      return t(`member-system-${String(get().userLevel())}`)
    },
    userLevel: () => {
      const { isPictureUser, userInfo, isStudentValid, isPersonal } = get()
      return Number(
        isPictureUser()
          ? userInfo?.pictureLever
          : isStudentValid() && isPersonal()
          ? UserLevel.DiamondStudent
          : userInfo?.tempMemberValidityTime
          ? UserLevel.DiamondAnimationGift
          : userInfo?.level,
      )
    },
    isStudentValid: () => {
      const { isStudent, userInfo } = get()
      return isStudent() && userInfo?.studentEndTime
        ? new Date().getTime() < new Date(userInfo?.studentEndTime).getTime()
        : false
    },
    isStudent: () => {
      return get().userInfo?.infoStatus === InfoStatus.Passed
    },
    isCertified: () => {
      const { userInfo } = get()
      if (!userInfo) return false
      return UserCertifiedStatus.includes(userInfo.authStatus)
    },
  }
}

export const useGlobalStore = create<IGlobalStore>()(
  devtools(
    subscribeWithSelector(
      composeMiddleware([immer], (set: StoreSet<IGlobalStore>, get: StoreGet<IGlobalStore>) => ({
        ...initialState(),
        ...getters(get),
        ...actions(set, get),
      })),
    ),
  ),
)
